<template>
    <side-sheet
        ref="ruleSideSheet"
        :size="3"
        label="Virtuele collega ai regel formulier"
        description="Hier kan je een ai regel aanmaken of wijzigen"
    >
        <form-validation v-slot="{ bindings: validationBindings }">
            <flex-container
                :gap="null"
                class="rule-detail"
                direction="column"
                no-wrap
            >
                <flex-container
                    class="rule-detail-fixed"
                    :padding="Size.md"
                    :gap="null"
                    :color="Color.gray100"
                >
                    <text-heading>
                        Automatische A.I. instructie
                    </text-heading>
                    <text-description :color="Color.gray800">
                        Maak een instructie aan voor het automatisch sturen van een e-mail.
                    </text-description>
                </flex-container>

                <virtual-colleague-message-block
                    class="rule-detail-fixed"
                >
                    Hoi {{ currentUser.name.firstName }}, ik ga je helpen met het aanmaken van een instructie.
                </virtual-colleague-message-block>

                <flex-container :gap="null" class="rule-detail-reactive">
                    <flex-container :padding="Size.md">
                        <input-group v-model="form.frequencies" :schema="array().min(1).max(1)">
                            <template #label>
                                <input-label>
                                    Wanneer wil je de mail ontvangen?
                                </input-label>
                                <input-label-description>
                                    De mail wordt op de eerste maandag van de gekozen periode na 13:00 verstuurd.
                                </input-label-description>
                            </template>
                            <template #input>
                                <checkbox-group-input
                                    :columns="3"
                                    :items="RuleFrequency.options"
                                />
                            </template>
                        </input-group>

                        <input-group v-model="form.teams" :schema="array().min(1)">
                            <template #label>
                                <input-label>
                                    Over welke teams wil je dat ik een analyse maak?
                                </input-label>
                            </template>
                            <template #input>
                                <!-- TODO: Skeleton -->
                                <team-multi-select-input :teams="teams.data" />
                            </template>
                        </input-group>

                        <input-group v-model="form.pipelines" :schema="array()">
                            <template #label>
                                <input-label>
                                    En over welke pijplijnen wil je dat ik een analyse maak?
                                </input-label>
                                <input-label-description>
                                    Als je geen pijplijnen selecteert, maak ik een analyse over alle pijplijnen.
                                </input-label-description>
                            </template>
                            <template #input>
                                <!-- TODO: Skeleton -->
                                <pipeline-multi-select-input :pipelines="availablePipelines" variant="pipeline" />
                            </template>
                        </input-group>
                    </flex-container>

                    <flex-container :padding="Size.md" :color="Color.gray100">
                        <input-group v-model="form.role" :schema="object().required()">
                            <template #label>
                                <input-label>
                                    Vanuit welke rol wil je dat ik de mail stuur?
                                </input-label>
                            </template>
                            <template #input>
                                <!-- TODO: Skeleton -->
                                <a-i-role-icon-radio-group-input />
                            </template>
                        </input-group>
                    </flex-container>

                    <flex-container
                        v-if="form.role?.description"
                        :padding="Size.md"
                        :color="Color.brandPurple"
                    >
                        <text-description :color="Color.white">
                            {{ form.role.description }}
                        </text-description>
                    </flex-container>

                    <flex-container :padding="Size.md">
                        <input-group v-model="form.recipients" :schema="array().min(1)">
                            <template #label>
                                <input-label>
                                    Naar wie wil je dat ik de mail stuur?
                                </input-label>
                            </template>
                            <template #input>
                                <!-- TODO: Skeleton -->
                                <user-multi-select-input :users="users.data" />
                            </template>
                        </input-group>
                    </flex-container>
                    <flex-container :padding="Size.xl" />
                    <flex-container :padding="Size.lg" />
                </flex-container>

                <flex-container
                    class="rule-detail-fixed"
                    :padding="Size.md"
                    :color="Color.gray100"
                    justify-content="flex-end"
                    align-items="center"
                >
                    <button-text @click="redirectToList">
                        Annuleren
                    </button-text>
                    <button-block
                        :disabled="!validationBindings.isValid.value"
                        @loading="save($event)"
                    >
                        Opslaan
                    </button-block>
                </flex-container>
            </flex-container>
        </form-validation>
    </side-sheet>
</template>

<script setup lang="ts">
    import { useRouter } from 'vue-router';
    import { computed, ref } from 'vue';
    import { SideSheet, Size, Color, InputLabelDescription, CheckboxGroupInput, type ButtonStateChangedEvent, ButtonState } from '@keyvue/one-ui';
    import useOverlayAsRoute from '@composables/useOverlayAsRoute';
    import VirtualColleagueMessageBlock from '@components/Blocks/VirtualColleagueMessageBlock.vue';
    import TeamMultiSelectInput from '@components/Inputs/TeamMultiSelectInput.vue';
    import useClient from '@composables/useClient';
    import { CreateRuleCommand, ChangeRuleCommand, Rule, RuleFrequency, RuleId, Pipeline, Team, User, AIRole } from '@keyvue/one-api-client';
    import PipelineMultiSelectInput from '@components/Inputs/PipelineMultiSelectInput.vue';
    import UserMultiSelectInput from '@components/Inputs/UserMultiSelectInput.vue';

    import AIRoleIconRadioGroupInput from '@components/Inputs/AIRoleIconRadioGroupInput.vue';
    import useNotify from '@composables/useNotify';
    import useEventBus from '@composables/useEventBus';
    import { array, object } from 'yup';

    const ruleSideSheet = ref();
    const router = useRouter();
    const eventBus = useEventBus();
    const { client, currentCompany, currentUser, getReactiveCollection } = useClient();
    const { notifySaving } = useNotify();
    const ruleId = computed(() => router.currentRoute.value.params?.ruleId);

    const form = ref<{
        frequencies: { label: string, value: RuleFrequency }[],
        teams: Team[],
        pipelines: Pipeline[],
        recipients: User[],
        role: AIRole | null,
    }>({
        frequencies: [],
        teams: [],
        pipelines: [],
        recipients: [],
        role: null,
    });

    const [teams, users, pipelinesCollection] = await Promise.all([
        client.getSubResources(currentCompany.value, Team, 'teams', { pagination: false }),
        client.getSubResources(currentCompany.value, User, 'users', { pagination: false }),
        await getReactiveCollection(Pipeline, { company: currentCompany.value.id, active: true, pagination: false }),
    ]);

    const pipelines = computed(() => pipelinesCollection.data.value);

    const availablePipelines = computed(() => {
        return pipelines.value.filter((pipeline) => {
            return form.value.teams.some((team) => pipeline.hasTeam(team as Team));
        });
    });

    if (ruleId.value) {
        const rule = await client.get<Rule>(Rule, ruleId.value as string);
        form.value.frequencies = rule.frequencies
            .map((ruleFrequency) => (
                RuleFrequency.options.find((option) => option.value.equals(ruleFrequency))
            )) as { label: string, value: RuleFrequency }[];
        form.value.teams = rule.teams;
        form.value.pipelines = rule.pipelines;
        form.value.recipients = rule.recipients;
        form.value.role = rule.role;
    }

    useOverlayAsRoute({
        overlay: ruleSideSheet,
        isSideSheet: true,
        redirectMethod: async () => {
            await router.push({ name: 'settings-virtual-colleague-ai-rules' });
        },
    });

    const redirectToList = async () => {
        router.push({ name: 'settings-virtual-colleague-ai-rules' });
    };

    const save = async (event: ButtonStateChangedEvent) => {
        let saveMessage;

        if (ruleId.value) {
            saveMessage = 'De regel wordt opgeslagen.';
        } else {
            saveMessage = 'De regel wordt aangemaakt.';
        }

        const closeNotification: () => void = notifySaving(saveMessage, 0);

        let command;

        if (ruleId.value) {
            command = ChangeRuleCommand.fromRule(Rule.fromObject({
                id: ruleId.value,
                ...form.value,
                frequencies: form.value.frequencies.map((ruleFrequency) => (ruleFrequency.value)),
            }));
        } else {
            command = CreateRuleCommand.fromRule(Rule.fromObject({
                id: RuleId.generate(),
                ...form.value,
                frequencies: form.value.frequencies.map((ruleFrequency) => (ruleFrequency.value)),
            }), currentCompany.value.id);
        }

        await client.dispatch(command);

        closeNotification();
        event.button.setState(ButtonState.completed);

        eventBus.dispatch('resource-manipulated', 'Rule');

        redirectToList();
    };
</script>

<style scoped lang="scss">
.rule-detail {
    height: 100vh;
}

.rule-detail-fixed {
    flex-grow: 0 !important;
    flex-shrink: 0;
}

.rule-detail-reactive {
    flex-shrink: 1;
    flex-grow: 1;
    overflow-y: auto;
}
</style>
